import { useNavigate } from "@solidjs/router";
import { dispatchGlobalEvent } from "@storytell/events";
import type { ContextResponse, GrantOp, ProjectId, ScopeKey } from "@storytell/types";
import { isServer } from "solid-js/web";
import type { ActiveContextStore } from "../activeContext/activeContextStore";
import type { TenantsStore } from "../tenants/tenantsStore";

export type UserContextService = ReturnType<typeof createUserContextService>;

const STORAGE_KEY_RECENT_PROJECT_IDS = "userContext.recentProjectIds";
const MAX_RECENT_PROJECT_IDS = 50;

export const createUserContextService = (deps: { activeContext: ActiveContextStore; tenants: TenantsStore }) => {
  const navigate = useNavigate();

  const get = (): ContextResponse | undefined => deps.activeContext.activeContext();

  const activeContext = () => deps.activeContext.activeContext();

  const canAt = (scope: ScopeKey, resource: string, op: GrantOp): boolean =>
    deps.activeContext.canAt(scope, resource, op);

  const recordRecentProject = (projectId: ProjectId) => {
    if (isServer || typeof localStorage === "undefined") return;
    try {
      const raw = localStorage.getItem(STORAGE_KEY_RECENT_PROJECT_IDS);
      const parsed: unknown = raw ? JSON.parse(raw) : [];
      const prev = Array.isArray(parsed) ? parsed.filter((id): id is string => typeof id === "string") : [];
      const next = [projectId, ...prev.filter((id) => id !== projectId)].slice(0, MAX_RECENT_PROJECT_IDS);
      localStorage.setItem(STORAGE_KEY_RECENT_PROJECT_IDS, JSON.stringify(next));
    } catch {
      // Ignore localStorage errors
    }
  };

  // Counterpart to recordRecentProject: once the backend has denied this
  // project, recents must stop offering it as somewhere to go back to, the same
  // way the last-project cookie is dropped on the denial.
  const forgetRecentProject = (projectId: string) => {
    if (isServer || typeof localStorage === "undefined") return;
    try {
      const next = getRecentProjectIds().filter((id) => id !== projectId);
      localStorage.setItem(STORAGE_KEY_RECENT_PROJECT_IDS, JSON.stringify(next));
    } catch {
      // Ignore localStorage errors
    }
  };

  const switchToProject = (projectId: ProjectId, options?: { preserveNavigation?: boolean }) => {
    recordRecentProject(projectId);
    void deps.tenants.persistSelection(projectId);
    dispatchGlobalEvent("user-context-changed", {
      projectId,
      preserveChat: options?.preserveNavigation ?? false,
    });
    if (!options?.preserveNavigation) {
      navigate(`/project/${projectId}`);
    }
  };

  const getRecentProjectIds = (): string[] => {
    if (isServer) return [];
    try {
      const raw = typeof localStorage !== "undefined" ? localStorage.getItem(STORAGE_KEY_RECENT_PROJECT_IDS) : null;
      const arr: unknown = raw ? JSON.parse(raw) : [];
      return Array.isArray(arr) ? arr.filter((id): id is string => typeof id === "string") : [];
    } catch {
      return [];
    }
  };

  return {
    get,
    activeContext,
    canAt,
    switchToProject,
    recordRecentProject,
    forgetRecentProject,
    getRecentProjectIds,
  };
};
